import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EXTERNAL_LINKS, FEEDBACK_URL } from '../data/externalLinks';
import './AppDrawer.css';

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ExternalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7"></line>
    <polyline points="7 7 17 7 17 17"></polyline>
  </svg>
);

const FeedbackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const GROUPS = ['Academics', 'Campus'];

const AppDrawer = ({ isOpen, onClose, onOpen }) => {
  const { user, moodleData } = useAuth();
  const drawerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const touchRef = useRef({ startX: 0, startY: 0, tracking: false, mode: null });

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      setTimeout(() => {
        drawerRef.current?.querySelector('button, a[href]')?.focus();
      }, 50);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Unified swipe handler: swipe left anywhere to open, swipe right anywhere to close
  useEffect(() => {
    const t = touchRef.current;

    const onTouchStart = (e) => {
      t.startX = e.touches[0].clientX;
      t.startY = e.touches[0].clientY;
      t.tracking = false;
      t.mode = isOpen ? 'close' : 'open';
    };

    const onTouchMove = (e) => {
      const deltaX = e.touches[0].clientX - t.startX;
      const deltaY = e.touches[0].clientY - t.startY;

      if (!t.tracking) {
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
        if (t.mode === 'open' && (Math.abs(deltaY) > Math.abs(deltaX) || deltaX > 0)) { t.mode = null; return; }
        if (t.mode === 'close' && (Math.abs(deltaY) > Math.abs(deltaX) || deltaX < 0)) { t.mode = null; return; }
        t.tracking = true;
      }

      if (t.mode === 'open') {
        e.preventDefault();
        const progress = Math.min(Math.abs(deltaX), 320);
        if (drawerRef.current) {
          drawerRef.current.style.transition = 'none';
          drawerRef.current.style.transform = `translateX(calc(100% - ${progress}px))`;
        }
      } else if (t.mode === 'close') {
        e.preventDefault();
        const offset = Math.max(0, deltaX);
        if (drawerRef.current) {
          drawerRef.current.style.transition = 'none';
          drawerRef.current.style.transform = `translateX(${offset}px)`;
        }
      }
    };

    const onTouchEnd = (e) => {
      if (!t.tracking) { t.mode = null; return; }
      const deltaX = e.changedTouches[0].clientX - t.startX;
      const mode = t.mode;
      t.mode = null;
      t.tracking = false;

      if (drawerRef.current) {
        drawerRef.current.style.transition = '';
        drawerRef.current.style.transform = '';
      }

      if (mode === 'open' && Math.abs(deltaX) > 60) onOpen?.();
      if (mode === 'close' && deltaX > 80) onClose();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isOpen, onOpen, onClose]);

  const handleTabKey = (e) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;
    const focusables = Array.from(
      drawerRef.current.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const getInitial = () => {
    const name = user?.firstName || user?.displayName || moodleData?.user?.firstname || user?.email || '';
    return name ? name[0].toUpperCase() : '?';
  };

  const getName = () => {
    if (user?.firstName || user?.lastName) {
      return [user.firstName, user.lastName].filter(Boolean).join(' ');
    }
    if (moodleData?.user?.firstname || moodleData?.user?.lastname) {
      return [moodleData.user.firstname, moodleData.user.lastname].filter(Boolean).join(' ');
    }
    return user?.displayName || moodleData?.user?.fullname || '';
  };

  const getEmail = () => user?.email || moodleData?.user?.email || '';

  return createPortal(
    <div
      className={`drawer-overlay${isOpen ? ' drawer-overlay--open' : ''}`}
      aria-hidden={!isOpen ? 'true' : undefined}
      onClick={isOpen ? onClose : undefined}
    >
      <div
        ref={drawerRef}
        className={`drawer${isOpen ? ' drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleTabKey}
      >
        <div className="drawer-header">
          <Link
            to="/user-details"
            className="drawer-user"
            onClick={onClose}
            aria-label="Go to account page"
          >
            <div className="drawer-avatar" aria-hidden="true">{getInitial()}</div>
            <div className="drawer-user-info">
              {getName() && <span className="drawer-name">{getName()}</span>}
              <span className="drawer-email">{getEmail()}</span>
            </div>
          </Link>
          <button
            className="drawer-close"
            onClick={onClose}
            aria-label="Close navigation menu"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="drawer-links" aria-label="External links">
          {GROUPS.map(group => (
            <div key={group} className="drawer-group">
              <div className="drawer-group-title">{group}</div>
              {EXTERNAL_LINKS.filter(l => l.group === group).map(({ label, to, Icon }) => (
                <Link
                  key={label}
                  to={to}
                  className="drawer-link"
                  onClick={onClose}
                  aria-label={`${label}, opens external site`}
                >
                  <span className="drawer-link-icon" aria-hidden="true"><Icon /></span>
                  <span className="drawer-link-label">{label}</span>
                  <span className="drawer-link-ext" aria-hidden="true"><ExternalIcon /></span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="drawer-footer">
          <Link to="/user-details" className="drawer-account-btn" onClick={onClose}>
            My Account
          </Link>

          <a
            href={FEEDBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="drawer-feedback"
            onClick={onClose}
          >
            <span className="drawer-feedback-icon" aria-hidden="true"><FeedbackIcon /></span>
            <span className="drawer-feedback-text">
              <span className="drawer-feedback-label">App Feedback</span>
              <span className="drawer-feedback-desc">Report a bug or suggest a feature</span>
            </span>
            <span className="drawer-link-ext" aria-hidden="true"><ExternalIcon /></span>
          </a>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default AppDrawer;
